import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, CONTACT } from "@/lib/site";
import { useGo } from "@/lib/nav";
import { EASE } from "./Motion";

export const Nav = ({ ready }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const goTo = useGo();

  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 40);
    f();
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);

  const go = (item) => { setOpen(false); goTo(item); };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={ready ? { y: 0, opacity: 1 } : {}}
        transition={{ duration: 1, delay: 0.5, ease: EASE }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${scrolled ? "border-b border-white/[0.08] bg-[#0A0A0A]/90 backdrop-blur-xl" : "border-b border-transparent"}`}
      >
        <div className="mx-auto flex h-20 max-w-[1500px] items-center justify-between px-5 md:px-10">
          <button data-testid="nav-brand-logo" onClick={() => go("top")} aria-label="Newland Construction home">
            <Logo />
          </button>
          <nav className="hidden items-center gap-7 xl:flex">
            {NAV.map((n) => (
              <button key={n.id} data-testid={`nav-link-${n.id}`} onClick={() => go(n)}
                className="group relative font-mono text-[0.68rem] uppercase tracking-[0.2em] text-neutral-400 transition-colors hover:text-white">
                {n.label}
                <span className="absolute -bottom-1 left-0 h-px w-full origin-right scale-x-0 bg-white transition-transform duration-500 group-hover:origin-left group-hover:scale-x-100" />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a data-testid="nav-phone-button" href={CONTACT.phoneHref} className="hidden items-center gap-2 font-mono text-xs text-neutral-300 transition-colors hover:text-white md:flex">
              <Phone className="h-3.5 w-3.5" /> {CONTACT.phone}
            </a>
            <button data-testid="nav-quote-button" onClick={() => go("quote")}
              className="group flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#0A0A0A] transition-colors hover:bg-neutral-200">
              Free estimate
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </button>
            <button data-testid="nav-mobile-menu-button" onClick={() => setOpen(true)} className="rounded-full border border-white/10 p-2.5 text-neutral-100 xl:hidden" aria-label="Open menu">
              <Menu className="h-4 w-4" />
            </button>
          </div>
        </div>
      </motion.header>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="fixed inset-0 z-[70] flex flex-col bg-[#0A0A0A] p-6"
          >
            <div className="flex items-center justify-between">
              <Logo />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="rounded-full border border-white/10 p-2.5 text-white" aria-label="Close menu">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-14 flex flex-col gap-4">
              {[...NAV, { id: "quote", label: "Free estimate" }].map((n, i) => (
                <motion.button key={n.id} data-testid={`mobile-link-${n.id}`} onClick={() => go(n)}
                  initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 + i * 0.06, ease: EASE, duration: 0.8 }}
                  className="text-left font-display text-4xl font-bold tracking-tight text-neutral-50">
                  <span className="mr-4 font-mono text-xs text-neutral-500">0{i + 1}</span>{n.label}
                </motion.button>
              ))}
            </div>
            <p className="mt-auto font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">{CONTACT.region}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
